import { SanityImageSource } from '@sanity/asset-utils'
import React, { FC, useMemo } from 'react'

import { ProfessionalResourceType, PublicResourceType } from '../../../../graphql/api'
import { MenuEntryContentFragment } from '../../../../graphql/gatsby'
import useUserCategory from '../../../../hooks/useUserCategory'
import { professionalCategories, ResourceTypeSlug } from '../../../../lib/library'
import {
  resourceCategoryUrlMapping,
  resourceTypeSanityMapping,
  resourceTypeUrlMapping,
} from '../../../../utils/libraryTools'
import ResourceLink from '../../../sanity-page/library/ResourceLink'

export interface MenuEntryLibraryResourceProps {
  libraryContent: NonNullable<MenuEntryContentFragment['libraryContent']>['content']
}

export const MenuEntryLibraryResource: FC<MenuEntryLibraryResourceProps> = ({ libraryContent }) => {
  const { userCategory } = useUserCategory()
  const { resourceType, resourceCategory, imageAsset } = useMemo(() => {
    const resourceTypeSlug = libraryContent
      ? (resourceTypeSanityMapping[libraryContent.__typename] as ResourceTypeSlug)
      : null

    if (!resourceTypeSlug) {
      return { resourceType: null, resourceCategory: null, imageAsset: null }
    }

    const type = resourceTypeUrlMapping[resourceTypeSlug]
    const category = resourceCategoryUrlMapping[resourceTypeSlug]

    const asset =
      libraryContent?.__typename === 'SanitySocialMediaResource'
        ? libraryContent?.asset
        : libraryContent?.thumbnail

    return {
      resourceType: type as ProfessionalResourceType | PublicResourceType,
      resourceCategory: category || '',
      imageAsset: (asset as SanityImageSource) || undefined,
    }
  }, [libraryContent])

  const hasAccess = useMemo(() => {
    const isProfessional = !!userCategory && professionalCategories.indexOf(userCategory) > -1
    const isPublicResourceType = Object.values(PublicResourceType).includes(
      resourceType as PublicResourceType,
    )

    return isProfessional || isPublicResourceType
  }, [userCategory, resourceType])

  if (!libraryContent || !resourceType || !hasAccess) {
    return null
  }

  return (
    <ResourceLink
      slug={libraryContent.slug?.current || ''}
      resourceType={resourceType}
      category={resourceCategory}
      className="h-auto mx-10 lg:mx-16 max-w-md lg:max-w-2xl"
      title={libraryContent.title || ''}
      shortDescription={libraryContent.shortDescription}
      publishedDate={libraryContent.publishedDate}
      orientation="horizontal"
      asset={imageAsset}
    />
  )
}
